import React from "react";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Stats from "@/components/Stats";
import Manifesto from "@/components/Manifesto";
import EditorialMarquee from "@/components/EditorialMarquee";
import Permits from "@/components/Permits";
import PistasSection from "@/components/PistasSection";
import TheoryTest from "@/components/TheoryTest";
import TeoricaSection from "@/components/TeoricaSection";
import CapSection from "@/components/CapSection";
import PuntosSection from "@/components/PuntosSection";
import Centers from "@/components/Centers";
import Reviews from "@/components/Reviews";
import BlogPreview from "@/components/BlogPreview";
import ContactForm from "@/components/ContactForm";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import MobileBottomNav from "@/components/MobileBottomNav";
import MobileFold from "@/components/MobileFold";
import { PUNTOS_EUR, CARNET_EUR } from "@/lib/courses";
import { usePricing } from "@/lib/usePricing";
import NextCourseBanner from "@/components/NextCourseBanner";
import { useSeo, JsonLd, SITE_URL, SOCIAL_PROFILES } from "@/lib/seo";
import { SEDES_SCHEMA } from "@/lib/sedesSchema";
import { FARO_LOGO, COMPANY_EMAIL } from "@/lib/faro";
import { GraduationCap, ScrollText, Truck, RotateCcw, MapPin, Star, Newspaper, Zap, ArrowUpRight, MessageCircle, Phone } from "lucide-react";

const IMG = {
  carnet: "/assets/recuperar-carnet-conducir-vigo.webp",
  permisos: "/assets/coche-practicas-carnet-b-autoescuelas-faro-vigo.webp",
  cap: "/assets/curso-cap-vigo-autoescuelas-faro.webp",
  puntos: "/assets/curso-recuperacion-puntos-vigo-plaza-america.webp",
  centros: "/assets/sedes-autoescuelas-faro-vigo.webp",
  test: "/assets/test-teorico-dgt-gratis-vigo.webp",
  teorica: "/assets/curso-intensivo-teorica-vigo.webp",
  opiniones: "/assets/opiniones-alumnos-autoescuelas-faro-vigo.webp",
  blog: "/assets/blog-autoescuelas-faro-vigo.webp",
};

export default function HomePage() {
  const { puntosEur, carnetEur } = usePricing();
  const priceP = puntosEur || PUNTOS_EUR;
  const priceC = carnetEur || CARNET_EUR;
  // Restore home SEO tags after visiting landings (useSeo overrides them client-side)
  useSeo({
    title: "Autoescuela en Vigo | Coche, Moto, Camión y CAP · Faro",
    description: "5 centros en Vigo, Nigrán y A Cañiza. Pistas propias donde te examina la DGT. Carnet de coche, moto, camión y autobús, cursos CAP y formación vial desde 1980.",
    canonical: `${SITE_URL}/`,
  });

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://autoescuelasfaro.com/#organization",
    name: "Autoescuelas Faro",
    legalName: "Automovilística Faro S.L.",
    vatID: "B36652170",
    taxID: "B36652170",
    url: SITE_URL,
    logo: FARO_LOGO.startsWith("http") ? FARO_LOGO : `${SITE_URL}${FARO_LOGO}`,
    telephone: "+34986292455",
    email: COMPANY_EMAIL,
    foundingDate: "1980",
    identifier: {
      "@type": "PropertyValue",
      propertyID: "Autorización DGT",
      value: "360024",
    },
    hasCredential: {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Autorización administrativa",
      name: "Centro de formación autorizado por la Dirección General de Tráfico nº 360024",
      recognizedBy: {
        "@type": "GovernmentOrganization",
        name: "Dirección General de Tráfico",
        url: "https://www.dgt.es",
      },
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      telephone: "+34986292455",
      email: COMPANY_EMAIL,
      availableLanguage: ["es"],
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: "Centro Comercial 3 Plaza América, locales 12, 13 y 14",
      addressLocality: "Vigo",
      postalCode: "36211",
      addressRegion: "Pontevedra",
      addressCountry: "ES",
    },
    sameAs: SOCIAL_PROFILES,
  };
  return (
    <div data-testid="home-page" className="pb-[calc(env(safe-area-inset-bottom)+72px)] sm:pb-0">
      <JsonLd data={orgSchema} />
      <JsonLd data={SEDES_SCHEMA} />
      <Header />
      <NextCourseBanner />
      <main>
        <Hero />
        <Stats />
        <div className="hidden sm:block"><Manifesto /></div>

        <MobileFold id="teorica" to="/curso-intensivo-teorica-vigo" eyebrow="Teórica en Vigo" title="Teórico en 1 semana" teaser="Intensivo presencial u online · Clases tradicionales · Plataforma con test" icon={Zap} imageAlt="Clase del curso intensivo de teorica de Autoescuelas Faro en Vigo" image={IMG.teorica}>
          <TeoricaSection />
        </MobileFold>

        <MobileFold id="permisos" to="/permisos" eyebrow="Catálogo completo" title="Permisos y carnets" teaser="13 permisos: coche, moto, camión, autobús, cuadriciclo, CAP, puntos…" icon={GraduationCap} imageAlt="Alumno haciendo practicas del carnet B en un coche de Autoescuelas Faro en Vigo" image={IMG.permisos}>
          <Permits />
        </MobileFold>

        <div className="hidden sm:block"><PistasSection /></div>
        <div className="hidden sm:block"><EditorialMarquee /></div>

        <MobileFold id="cap" to="/curso-cap-vigo" eyebrow="Profesionales" title="Cursos CAP" teaser="CAP Inicial · Continuo · Puente para transporte de mercancías y viajeros" icon={Truck} imageAlt="Camion de Autoescuelas Faro usado en los cursos CAP para conductores profesionales en Vigo" image={IMG.cap}>
          <CapSection />
        </MobileFold>

        {/* Banner destacado: HUB de recuperación de puntos y carnet en Vigo */}
        <section
          className="relative overflow-hidden"
          data-testid="home-puntos-hub-banner"
          style={{
            background:
              "linear-gradient(115deg, var(--faro-blue-deep) 0%, var(--faro-blue) 55%, #1f2fb5 100%)",
          }}
        >
          <div
            className="absolute inset-0 opacity-25 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 15% 25%, rgba(255,255,255,0.15), transparent 40%), radial-gradient(circle at 85% 70%, rgba(255,209,0,0.35), transparent 45%)",
            }}
          />
          <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-8 text-white">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 backdrop-blur px-3 py-1 text-[11px] uppercase tracking-[0.22em] font-black text-[var(--faro-yellow)]">
                <RotateCcw size={13} /> Centro autorizado DGT en Vigo
              </span>
              <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.05]">
                Recuperación de{" "}
                <span className="text-[var(--faro-yellow)]">Puntos y Carnet</span> en Vigo
              </h2>
              <p className="mt-3 text-base sm:text-lg text-white/85 max-w-2xl">
                Curso parcial <strong>concentrado en 1 día</strong> ({priceP}) para sumar hasta 4 puntos y curso total (20 h · {priceC})
                para recuperar el carnet. Aula oficial en Plaza América · convocatorias cada mes.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <a
                href="/recuperacion-de-puntos-vigo"
                data-testid="home-hub-cta-principal"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] font-black px-6 py-4 text-base shadow-lg shadow-black/25 transition-all hover:-translate-y-0.5 hover:brightness-95"
              >
                Ver información completa <ArrowUpRight size={18} />
              </a>
              <div className="grid grid-cols-2 gap-2">
                <a
                  href="https://wa.me/34637570075?text=Hola%2C%20quiero%20informaci%C3%B3n%20sobre%20los%20cursos%20de%20recuperaci%C3%B3n%20de%20puntos%20y%20del%20carnet%20en%20Vigo"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="home-hub-cta-whatsapp"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white font-black px-4 py-3 text-sm transition-all"
                >
                  <MessageCircle size={16} /> WhatsApp
                </a>
                <a
                  href="tel:+34986292455"
                  data-testid="home-hub-cta-call"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white text-[var(--faro-blue-deep)] font-black px-4 py-3 text-sm transition-all"
                >
                  <Phone size={16} /> Llamar
                </a>
              </div>
            </div>
          </div>
        </section>

        <MobileFold id="puntos" to="/recuperacion-de-puntos-vigo" eyebrow="Seguridad vial" title="Permiso por puntos" teaser="Curso parcial en 1 día + 3 h online · Cursos Tipo A y B" icon={RotateCcw} imageAlt="Aula del curso de recuperacion de puntos de Autoescuelas Faro en Plaza America, Vigo" image={IMG.puntos}>
          <PuntosSection />
        </MobileFold>

        <MobileFold id="test" to="/examen" eyebrow="Examen DGT" title="Test DGT + criterios oficiales" teaser="10 preguntas reales · Cómo se califica el examen · Consultar tu nota" icon={ScrollText} imageAlt="Test teorico de la DGT gratuito online de Autoescuelas Faro" image={IMG.test}>
          <TheoryTest />
        </MobileFold>

        <MobileFold id="centros" to="/centros" eyebrow="Nuestras sedes" title="5 centros en Vigo y área metro" teaser="Plaza América · Coruxo · Panxón · Nigrán · A Cañiza" icon={MapPin} imageAlt="Fachada de uno de los cinco centros de Autoescuelas Faro en Vigo y su area" image={IMG.centros}>
          <Centers />
        </MobileFold>

        <MobileFold id="opiniones" to="/opiniones" eyebrow="Opiniones" title="Lo que dicen nuestros alumnos" teaser="Opiniones reales de alumnos de Faro" icon={Star} imageAlt="Alumnos de Autoescuelas Faro despues de aprobar el examen de la DGT en Vigo" image={IMG.opiniones}>
          <Reviews />
        </MobileFold>

        <MobileFold id="blog" to="/blog" eyebrow="Blog & noticias" title="Aprende con los profes Faro" teaser="Seguridad vial · Trucos DGT · Actualidad" icon={Newspaper} imageAlt="Blog de seguridad vial, DGT y carnet de conducir de Autoescuelas Faro" image={IMG.blog}>
          <BlogPreview />
        </MobileFold>

        <ContactForm />
      </main>
      <Footer />
      <FloatingWhatsapp />
      <MobileBottomNav />
    </div>
  );
}
